import { LoadingButton } from '@/components/@extended';
import { Box, Button, Stack } from '@mui/material';
import { useListContext, useResourceContext, useTranslate } from 'ra-core';
import React, { ReactElement, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FilterContext, getFilterFormValues } from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import { ReloadOutlined } from '@ant-design/icons';
import { isEqual, isNull, isUndefined } from 'lodash';
import { SavedFiltersList } from './SavedFiltersList';
import { useListViewContext } from '@/components/ra-lists';
import { Sidebar } from '@/components/Layout/Sidebar';

interface FiltersInputProps {
  form: ReturnType<typeof useForm>;
  filters?: ReactElement | ReactElement[];
}

function FiltersInput(props: FiltersInputProps): ReactElement {
  const { form, filters } = props;
  const resource = useResourceContext();

  return (
    <FilterContext.Provider value={Array.isArray(filters) ? filters : [filters]}>
      <FormProvider {...form}>
        <Box px={'20px'}>
          {filters
            ? (Array.isArray(filters) ? filters : [filters]).map((filter: ReactElement, i) => {
                const props = { ...filter.props, display: 'label' };
                return React.isValidElement(filter) ? (
                  <Box key={i} mb={1}>
                    {React.cloneElement(filter, { ...props, resource })}
                  </Box>
                ) : null;
              })
            : null}
        </Box>
      </FormProvider>
    </FilterContext.Provider>
  );
}

interface FilterSidebarProps {
  filters?: ReactElement | ReactElement[];
  keepMounted?: boolean;
}

function FilterSidebarComp(props: FilterSidebarProps): ReactElement {
  const { filters, keepMounted = false } = props;
  const [loading, setLoading] = useState(false);
  const { setSidebarOpen, sidebarOpen } = useListViewContext();
  const { filterValues, setFilters } = useListContext();
  const translate = useTranslate();

  // values containes all the filters values and the default ones
  const values = useMemo(() => {
    if (!filters) return {};
    return (Array.isArray(filters) ? filters : [filters]).reduce((acc, filter) => {
      acc[filter.props.source] = filterValues?.[filter.props.source] ?? '';
      return acc;
    }, {} as Record<string, any>);
  }, [filters, filterValues]);

  const form = useForm({
    defaultValues: filterValues,
    values
  });
  const { handleSubmit, getValues, reset } = form;

  // Reapply filterValues when the URL changes or a user removes a filter
  useEffect(() => {
    const newValues = getFilterFormValues(getValues(), filterValues);
    const previousValues = getValues();
    if (!isEqual(newValues, previousValues)) {
      reset(newValues);
    }
    // The reference to the filterValues object is not updated when it changes,
    // so we must stringify it to compare it by value and also compare the reference.
    // This makes it work for both input values and filters applied directly through
    // the ListContext.setFilter (e.g. QuickFilter in the simple example)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filterValues), filterValues, getValues, reset]);

  const closeSidebar = useCallback(() => {
    if (setSidebarOpen) setSidebarOpen(false);
  }, [setSidebarOpen]);

  const onSubmit = React.useCallback(
    (data: any) => {
      // remove all empty || null || undefined values
      Object.keys(data).forEach((key) => {
        // if data[key] is empty, null or undefined, delete it. Some values can be boolean. So please handle it
        if (isNull(data[key]) || isUndefined(data[key]) || data[key] === '') {
          delete data[key];
        }
      });
      // @ts-ignore
      setFilters(data);
      closeSidebar();
    },
    [setFilters, closeSidebar]
  );

  const resetAllFields = useCallback(() => {
    reset({});
    // @ts-ignore
    setFilters({});
  }, [reset, setFilters]);

  const toggleLoading = useCallback(() => {
    setLoading((prev) => !prev);
    resetAllFields();
    setTimeout(() => setLoading(false), 1000);
  }, [resetAllFields]);

  return (
    <Sidebar sx={{ height: '100%' }} open={sidebarOpen} onClose={closeSidebar} keepMounted={keepMounted}>
      <Sidebar.Header>
        <Sidebar.HeaderText primary={translate('ra.action.add_filter')} />
        <Sidebar.HeaderAction>
          <LoadingButton
            variant="text"
            loading={loading}
            loadingPosition="start"
            // @ts-ignore
            startIcon={<ReloadOutlined />}
            onClick={toggleLoading}
          >
            {translate('ra.action.reset_filters')}
          </LoadingButton>
        </Sidebar.HeaderAction>
      </Sidebar.Header>
      <Sidebar.Content>
        <SavedFiltersList closeSidebar={closeSidebar} />
        <FiltersInput form={form} filters={filters} />
      </Sidebar.Content>
      <Sidebar.Footer>
        <Stack direction="row" spacing={2}>
          <Button
            fullWidth
            onClick={() => {
              reset({});
              closeSidebar();
            }}
          >
            {translate('ra.action.close')}
          </Button>
          <Button fullWidth variant="contained" color="primary" onClick={handleSubmit(onSubmit)}>
            {translate('ra.action.apply_filters')}
          </Button>
        </Stack>
      </Sidebar.Footer>
    </Sidebar>
  );
}

const FilterSidebar = memo(FilterSidebarComp);

export { FilterSidebar };
